// src/components/SaverCreditAccordion.jsx
import React, { useState } from "react";
import "./SaverCreditAccordion.css";
import SaverCreditTwoUp from "../AMain/SaverCreditTwoUp";
import SaversCreditSteps from "../Jordan/SaversCreditSteps";
import a1 from "./images/8880.png";
import a2 from "./images/Sch3.png";
import a3 from "./images/1040.png";
import { useTranslation } from "react-i18next";
import SaversAnsweringMachine from "../TryAgain/SaversAnsweringMachine";
import SaverCreditRateChart from "./SaverCreditRateChart";

const formatCurrency = (value) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

/* -------------------------------------------
   Alex Saver's Credit Example Card
------------------------------------------- */
const AlexSaverCreditCard = () => {
  const agi = 35000;
  const contribution = 2000;
  const taxBefore = 2078;
  const creditRate = 0.1; // 10%
  const creditBase = 2000;
  const saversCredit = creditBase * creditRate; // 200
  const finalTax = taxBefore - saversCredit; // 1878
  const { t } = useTranslation();

  return (
    <section className="sc-example">
      <h2 className="sc-example__title">{t("savers-example-sub")}</h2>

      <div className="hang">
        <span className="hang-bullet">-</span>
        <p className="hang-text">
          <strong>
            {t("name")}, {t("savers-example")}.
          </strong>
        </p>
      </div>

      <div className="sc-example__card">
        <div className="sc-example__row">
          <span>{t("savers-example-AGI")}</span>
          <span>{formatCurrency(agi)}</span>
        </div>

        <div className="sc-example__row sc-example__row--green">
          <span>{t("savers-example-retirement")}</span>
          <span>{formatCurrency(contribution)}</span>
        </div>

        <div className="sc-example__row">
          <span>{t("savers-example-taxesPre")}</span>
          <span>{formatCurrency(taxBefore)}</span>
        </div>

        <div className="sc-example__row sc-example__row--yellow">
          <span>{t("savers-example-savers")}</span>
          <span className="sc-example__amount--credit">
            -{formatCurrency(saversCredit)}
          </span>
        </div>

        <div className="sc-example__row sc-example__row--blue">
          <span>{t("savers-example-final")}</span>
          <span className="sc-example__amount--final">
            {formatCurrency(finalTax)}
          </span>
        </div>
      </div>

      <div className="sc-example__summary">{t("savers-example-conc")}</div>
    </section>
  );
};

/* -------------------------------------------
   Reusable collapsible panel
------------------------------------------- */
const SaverCreditPanel = ({ title, subtitle, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <section className="sc-panel">
      <button
        type="button"
        className="sc-panel__header"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
      >
        <div className="sc-panel__text">
          <h3 className="sc-panel__title">{title}</h3>
          {subtitle && <p className="sc-panel__subtitle">{subtitle}</p>}
        </div>
        <span className={`sc-panel__icon ${isOpen ? "sc-panel__icon--open" : ""}`}>
          ▾
        </span>
      </button>

      {isOpen && <div className="sc-panel__body">{children}</div>}
    </section>
  );
};

/* -------------------------------------------
   Main accordion
------------------------------------------- */
const SaverCreditAccordion = () => {
  const { t } = useTranslation();

  return (
    <div className="sc-accordion">
      {/* Panel 1 */}
      <SaverCreditPanel title={t("savers-heading")}>
        <div className="sc-panel__content">
          <SaversCreditSteps />
        </div>
      </SaverCreditPanel>

      {/* Panel 2 */}
      <SaverCreditPanel title={t("savers-2-heading")}>
        <div className="sc-panel__content">
          <p className="sc-section-title">
            <strong>{t("savers-0-2m")}</strong>
          </p>

          <ol className="sc-list">
            <li>{t("savers-0-2r1")}</li>
            <li>{t("savers-0-2r2")}</li>
            <li>{t("savers-0-2r3")}</li>
            <li>{t("savers-0-2r4")}</li>
 </ol>
    <ol >
            <li>
              <strong>{t("savers-0-2r5i")}</strong> {t("savers-0-2r5")}
            </li>

            <li className="sc-list--subtle">{t("savers-0-2r6")}</li>

            <li className="sc-list--subtle">
              <span
                onClick={() => (window.location.href = "")}
                style={{ cursor: "pointer", textDecoration: "underline" }}
              >
                <strong>{t("savers-0-2r7i")}</strong>
              </span>{" "}
              <strong>{t("savers-0-2r7")}</strong>
            </li>
          </ol>
        </div>
      </SaverCreditPanel>

      {/* Panel 3 */}
      <SaverCreditPanel title={t("savers-3-heading")}>
        <div className="sc-panel__content">
          <ul className="sc-list">
            <li>{t("savers-3-1")}</li>
            <li>{t("savers-3-2")}</li>
            <li className="sc-list__emphasis">
              <strong>{t("savers-3-sub")}</strong>
            </li>
          </ul>
        </div>
      </SaverCreditPanel>

      {/* Example */}
      <SaverCreditPanel title={t("savers-ex")} defaultOpen={true}>

              <AlexSaverCreditCard />
             
           </SaverCreditPanel>
  
       <SaverCreditRateChart />
   
 
      {/* IRS section */}
      <SaverCreditPanel title={t("savers-IRS-heading")} subtitle={t("savers-IRS-sub")}>
        <div className="sc-panel__content">
          <ol className="sc-list sc-ol">
          

            <li>
              <strong>{t("savers-IRS-forms")}</strong>
              <ul className="sc-list sc-list--nested">
                <li>
                  <strong>Form 8880</strong>: {t("savers-IRS-8880")}
                </li>
                <li>
                  <strong>Schedule 3</strong>: {t("savers-IRS-sch3")}
                </li>
                <li>
                  <strong>Form 1040</strong>: {t("savers-IRS-1040")}
                </li>
              </ul>
            </li>

            <li>
              <strong>{t("savers-IRS-flow-head")}</strong>
            </li>

            <li>
              {t("savers-IRS-flow")}{" "}
              <span className="sc-pill">Form 8880 → Schedule 3 → Form 1040.</span>
            </li>

            <li>
              <div className="sc-media"  style={{marginTop: "50px"}}>
                <span className="sc-pill">Computation in 8880  </span>             


<span>  <a
    href="https://www.irs.gov/pub/irs-pdf/f8880.pdf"
    target="_blank"
    rel="noopener noreferrer"
  >
   <strong>IRS Form 8880 – Credit for Qualified Retirement Savings Contributions</strong> 
  </a></span>

                <img src={a1} alt="Form 8880 example" width="800" />
              </div>
            </li>

            <li>
              <div className="sc-media" style={{marginTop: "50px"}}>
                <span className="sc-pill">Schedule 3</span>
                <p>
 
</p>

                <img src={a2} alt="Schedule 3 example" width="800" />
              </div>
            </li>

            <li>
              <div className="sc-media"  style={{marginTop: "50px"}}>
                <span className="sc-pill">Form 1040</span>
                <p>

</p>

                <img src={a3} alt="Form 1040 example" width="800" />
              </div>
            </li>
          </ol>
        </div>
      </SaverCreditPanel>

    </div>
  );
};

export default SaverCreditAccordion;